/**
 * FilterModule
 *
 * Manages product filtering interactions, DOM updates, and UI visibility.
 * This module handles filter input changes, resets filters, and updates the
 * product list based on applied filters.
 *
 * @module FilterModule
 */
import { FilterOpenedEvent } from '@theme/events';

(function () {
  'use strict';

  /**
   * Exposes the FilterModule public API for external use.
   * @namespace POV.filter
   */
  window.POV.filter = {
    apply: applyFilters,
    reset: resetFilters,
    openPanel: openFilterPanel,
    closePanel: closeFilterPanel,
    disable: disable,
    enable: enable,
    updateFilters: updateFilters,
    initFilters: initFilters,
  };

  /**
   * DOM selectors used in the FilterModule.
   * @constant {Object<string, string>}
   */
  const SELECTORS = {
    host: '.collection-products',//'#shopify-section-collection-products',
    filterComponent: '[data-filter-form]',
    inputs: '[data-filter-input]',
    priceMin: '[name="filter.v.price.gte"]',
    priceMax: '[name="filter.v.price.lte"]',
    priceInputs: '[name="filter.v.price.gte"], [name="filter.v.price.lte"]',
    sortInputs: '[data-filter-sort]',
    searchTypeInputs: '[data-search-type]:checked',
    collectionHeader: '[data-collection-header]',
    panel: '[data-filter-drawer]',
    scrim: '#Scrim',
    reset: '[data-filter-reset]',
    open: '[data-filter-trigger]',
    details: 'details[data-filter-details]',
    close: '[data-filter-close], #Scrim',
    trustPilotWidget: '.trustpilot-widget',
  };

  /**
   * Events emitted by the FilterModule.
   * @constant {Object<string, string>}
   */
  const EVENTS = {
    change: 'filter:change',
    reset: 'filter:reset',
    open: 'filter:open',
    close: 'filter:close'
  };

  // Determine if the current page is a search page
  const isSearchPage = document.getElementById('shopify-section-main-search') !== null;

  // Cache DOM elements
  const collectionHeaderElement = document.querySelector(SELECTORS.collectionHeader);
  const panelElement = document.querySelector(SELECTORS.panel);
  const scrimElement = document.querySelector(SELECTORS.scrim);

  /**
   * Initializes all filter input event listeners, resets, open/close actions, and filter visibility logic.
   * Should be called on DOMContentLoaded and after filter DOM updates.
   * @function
   */
  function initFilters() {
    const filterInputNodes = Array.from(document.querySelectorAll(SELECTORS.inputs));
    const priceInputNodes = Array.from(document.querySelectorAll(SELECTORS.priceInputs));
    const sortInputNodes = Array.from(document.querySelectorAll(SELECTORS.sortInputs));

    // Add event listeners for sort inputs
    sortInputNodes.forEach((input) => {
      input.addEventListener('change', handleInputChange);
    });

    // Add debounced input event listeners to filter inputs
    filterInputNodes.forEach((inputNode) => {
      const delay = isSearchPage ? 16 : 300;
      const debouncedApplyFilters = debounce((e) => handleInputChange(e), delay);
      inputNode.addEventListener('input', debouncedApplyFilters);
    });

    // Price inputs apply on blur (the iOS numeric keypad's "Done" blurs) or on Enter.
    priceInputNodes.forEach((input) => {
      // XXX: handle iOS select issue
      input.addEventListener('blur', handlePriceInputBlur);
      input.addEventListener('keydown', handlePriceInputKeydown);
    });

    // Add event listeners for reset, open, and close actions
    document.querySelectorAll(SELECTORS.reset).forEach((button) => {
      button.addEventListener('click', resetFilters);
    });

    document.querySelectorAll(SELECTORS.close).forEach((button) => {
      button.addEventListener('click', closeFilterPanel);
    });

    document.querySelectorAll(SELECTORS.open).forEach((button) => {
      button.addEventListener('click', openFilterPanel);
    });

    // Dispatch FilterOpenedEvent when the drawer opens on mobile. The details
    // element survives filter re-renders, so guard against re-binding on the
    // initFilters calls that follow each update. On desktop the drawer is
    // force-open, so only a toggle below the L breakpoint counts as an open.
    document.querySelectorAll(SELECTORS.details).forEach((details) => {
      if (details.dataset.filterToggleBound) return;
      details.dataset.filterToggleBound = 'true';
      details.addEventListener('toggle', () => {
        if (!details.open || !matchMedia(`(max-width: ${window.POV.Breakpoints.L})`).matches) return;
        document.dispatchEvent(new FilterOpenedEvent());
      });
    });

    initFilterVisibility();
  }

  // Listen for browser navigation events (back/forward)
  window.addEventListener('popstate', handlePopState);

  document.addEventListener('DOMContentLoaded', initFilters);

  /**
   * Initializes filter visibility on first load or after filter updates.
   * @private
   */
  function initFilterVisibility() {
    if (isSearchPage) {
      handleFilterVisibility(document);
    }
  }

  /**
   * Handles filter input changes, updates the URL, and applies filters.
   * @param {Event} [event]
   */
  function handleInputChange(event) {
    const params = paramsFromInput();
    const url = createUrl(params);
    updateUrl(url);
    applyFilters(params, event && event.target);
  }

  /**
   * Extracts the current filter values from inputs and constructs a URLSearchParams object.
   * @returns {URLSearchParams}
   */
  function paramsFromInput() {
    const checkedInputs = Array.from(document.querySelectorAll(`${SELECTORS.inputs}:checked`)).filter(input => !input.disabled);
    const priceInputNodes = Array.from(document.querySelectorAll(SELECTORS.priceInputs)).filter(input => Number(input.value) > 0);
    const searchTypeInputNodes = Array.from(document.querySelectorAll(SELECTORS.searchTypeInputs));
    const sortInputNodes = Array.from(document.querySelectorAll(SELECTORS.sortInputs));
    const searchType = searchTypeInputNodes.length > 0 ? searchTypeInputNodes[0].value : '';

    const activeInputNodes = searchType === 'article' 
      ? [...searchTypeInputNodes]
      : [...checkedInputs, ...priceInputNodes, ...sortInputNodes, ...searchTypeInputNodes];

    const existingParams = new URLSearchParams(window.location.search);
    const searchQueryParam = existingParams.get('q');
    const params = new URLSearchParams();
    if (searchQueryParam) params.set('q', searchQueryParam);

    activeInputNodes.forEach((input) => {
      if (input.value) {
        params.append(input.name, input.value);
      }
    });

    return params;
  }

  /**
   * Captures scroll state and relative position of the toggled filter input.
   * @param {HTMLElement} toggledInput
   * @returns {Object|null}
   */
  function captureFilterScrollState(toggledInput) {
    if (!toggledInput) return null;
    const filterContainer = toggledInput.closest(SELECTORS.panel);
    if (!filterContainer) return null;
    const containerRect = filterContainer.getBoundingClientRect();
    const inputRect = toggledInput.getBoundingClientRect();
    return {
      containerSelector: SELECTORS.panel,
      inputName: toggledInput.name,
      inputValue: toggledInput.value,
      offsetTop: inputRect.top - containerRect.top,
      scrollTop: filterContainer.scrollTop,
    };
  }

  /**
   * Restores scroll state to keep the toggled input at the same relative position.
   * @param {Object} state
   */
  function restoreFilterScrollState(state) {
    if (!state) return;
    const filterContainer = document.querySelector(state.containerSelector);
    if (!filterContainer) return;
    const newInput = Array.from(filterContainer.querySelectorAll('.custom-filter-input')).find(
      input => input.name === state.inputName && input.value === state.inputValue
    );
    if (newInput) {
      const containerRect = filterContainer.getBoundingClientRect();
      const inputRect = newInput.getBoundingClientRect();
      const newOffsetTop = inputRect.top - containerRect.top;
      filterContainer.scrollTop += (newOffsetTop - state.offsetTop);
    } else {
      filterContainer.scrollTop = state.scrollTop;
    }
  }

  /**
   * Applies the selected filters, updates the URL, and fetches the filtered product list.
   * @async
   * @param {URLSearchParams} [params=new URLSearchParams()]
   * @param {HTMLElement|null} [toggledInput=null]
   * @returns {Promise<void>}
   */
  async function applyFilters(params = new URLSearchParams(), toggledInput = null) {
    const productContainer = document.querySelector(SELECTORS.host);
    productContainer.classList.add('filter-loading');
    if (collectionHeaderElement) {
      collectionHeaderElement.classList.add('hidden');
    }

    // Capture scroll state if toggled input given
    let filterScrollState = null;
    if (toggledInput) {
      filterScrollState = captureFilterScrollState(toggledInput);
    }

    try {
      const isSearchPage = document.getElementById('shopify-section-main-search') !== null;
      const sectionWrapper = productContainer.closest('.shopify-section');
      const sectionHandle = sectionWrapper ? sectionWrapper.id.replace('shopify-section-', '') : (isSearchPage ? 'main-search' : 'collection');
      const searchType = params.get('type') || 'product';
      if (isSearchPage) {
        disable();
      }

      const response = await fetch(`${window.location.pathname}?${params.toString()}&sections=${sectionHandle}`);
      const data = await response.json();

      if (isSearchPage) {
        searchType === 'article' ? disable() : enable();
      }

      await updateResults(data, isSearchPage, searchType, filterScrollState, sectionHandle);
      reinitTrustPilotWidgets();
      //POV.emit(EVENTS.change, window.POV.filter);
      //POV.Collapsible.reinit();
    } catch (error) {
      console.error('Error fetching filtered products:', error);
    }
  }

  /**
   * Updates the product list section with new HTML content.
   * Accepts a scroll state object and restores scroll position if given.
   *
   * @async
   * @param {Object} responseData - JSON response containing updated HTML content.
   * @param {boolean} isSearchPage
   * @param {string} searchType
   * @param {Object} filterScrollState
   * @param {string} sectionHandle
   */
  async function updateResults(responseData, isSearchPage, searchType, filterScrollState, sectionHandle) {
    let html;
    let newFilter;
    const responseKey = sectionHandle || (isSearchPage ? 'main-search' : 'collection');

    const doc = new DOMParser().parseFromString(responseData[responseKey], 'text/html');
    html = doc.querySelector(SELECTORS.host).outerHTML;
    newFilter = doc.querySelector(SELECTORS.filterComponent);
    handleFilterVisibility(doc);

  
    const oldFilter = document.querySelector(SELECTORS.filterComponent);
    const oldPanel = oldFilter.querySelector(SELECTORS.panel);

    const focusedInput = document.activeElement;
    const focusState = focusedInput && focusedInput.closest(SELECTORS.panel)
      ? { name: focusedInput.name, value: focusedInput.value }
      : null;

    oldPanel.innerHTML = newFilter.querySelector(SELECTORS.panel).innerHTML;

    if (focusState) {
      const restoredInput = oldPanel.querySelector('[name="' + focusState.name + '"]');
      if (restoredInput) {
        restoredInput.value = focusState.value;
      }
    }

    initFilters();
    // Restore scroll position for filter panel
    restoreFilterScrollState(filterScrollState);
    
    const container = document.querySelector(SELECTORS.host);
    container.outerHTML = html;
  }

  /**
   * Constructs a new URL with the provided query parameters.
   * @param {URLSearchParams} params
   * @returns {string}
   */
  function createUrl(params) {
    const baseUrl = window.location.href.split('?')[0];
    return `${baseUrl}?${params.toString()}`;
  }

  /**
   * Updates the browser's URL without reloading the page.
   * @param {string} url
   */
  function updateUrl(url) {
    window.history.pushState(null, '', url);
  }

  /**
   * Resets all filter inputs to their initial state and reapplies the filters.
   */
  function resetFilters() {
    const filterInputNodes = Array.from(document.querySelectorAll(SELECTORS.inputs));
    filterInputNodes.forEach((input) => {
      const initialValue = input.getAttribute('data-filter-initial-value');
      if (input.type === 'radio' || input.type === 'checkbox') {
        input.checked = initialValue === 'checked';
      } else if (input.type === 'number' || input.type === 'text') {
        input.value = initialValue || '';
      }
    });

    const existingParams = new URLSearchParams(window.location.search);
    const searchQueryParam = existingParams.get('q');
    const searTypeParam = existingParams.get('type') || 'product';
    const params = new URLSearchParams();
    if (searchQueryParam) params.set('q', searchQueryParam);
    if (searTypeParam) params.set('type', searTypeParam);

    updateUrl(createUrl(params));
    applyFilters(params);
    //POV.emit(EVENTS.reset, window.POV.filter);
  }

  /**
   * Opens the filter UI panel by setting the appropriate ARIA attributes.
   */
  function openFilterPanel() {
    panelElement.setAttribute('aria-hidden', 'false');
    scrimElement.setAttribute('aria-hidden', 'false');
    document.querySelector('[data-filter-trigger]').setAttribute('aria-expanded', 'true');
    document.querySelector('[data-filter-drawer]').setAttribute('aria-hidden', 'false');
    //POV.emit(EVENTS.open, window.POV.filter);
  }

  /**
   * Closes the filter UI panel by setting the appropriate ARIA attributes.
   */
  function closeFilterPanel() {
    const panelElement = document.querySelector(SELECTORS.panel);
    const scrimElement = document.querySelector(SELECTORS.scrim);
    panelElement.setAttribute('aria-hidden', 'true');
    scrimElement.setAttribute('aria-hidden', 'true');
    document.querySelector('[data-filter-trigger]').setAttribute('aria-expanded', 'false');
    document.querySelector('[data-filter-drawer]').setAttribute('aria-hidden', 'true');
    //POV.emit(EVENTS.close, window.POV.filter);
  }

  /**
   * Handles the browser's popstate event by reapplying filters based on URL parameters.
   */
  function handlePopState() {
    const params = new URLSearchParams(document.location.search);

    // Reset all checked filter inputs.
    const checkedInputs = document.querySelectorAll(`${SELECTORS.inputs}:checked`);
    checkedInputs.forEach((input) => (input.checked = false));

    // Update filter inputs based on the URL parameters.
    for (const [name, value] of params.entries()) {
      const inputElement =
        document.querySelector(`[name='${name}'][value='${value}']`) ||
        document.querySelector(`[name='${name}']`);
      if (!inputElement) continue;

      if (inputElement.type === 'radio' || inputElement.type === 'checkbox') {
        inputElement.checked = true;
      } else if (inputElement.type === 'number' || inputElement.type === 'select-one') {
        inputElement.value = value;
      }
    }

    applyFilters(params);
  }

  /**
   * Re-initializes Trustpilot widgets after DOM updates.
   */
  function reinitTrustPilotWidgets() {
    try {
      if (!window.Trustpilot || typeof window.Trustpilot.loadFromElement !== 'function') return;
      const trustPilotWidgetNodes = Array.from(document.querySelectorAll(SELECTORS.trustPilotWidget));
      trustPilotWidgetNodes.forEach(widget => {
        window.Trustpilot.loadFromElement(widget);
      });
    } catch (error) {
      console.error('Error reiniting Trustpilot widgets:', error);
    }
  }

  /**
   * Validates the price range (min may not exceed max) and applies the filters.
   * Shared by the blur and Enter paths of the price inputs.
   */
  function applyPriceRange() {
    const priceMinInput = document.querySelector(SELECTORS.priceMin);
    const priceMaxInput = document.querySelector(SELECTORS.priceMax);

    if (Number(priceMinInput.value) > Number(priceMaxInput.value)) {
      priceMinInput.value = Number(priceMaxInput.value) - 1;
    }

    handleInputChange();
  }

  /**
   * Applies the price range when focus leaves the price inputs. Deferred a tick so
   * tabbing from min to max does not apply half a range.
   */
  function handlePriceInputBlur() {
    setTimeout(() => {
      const active = document.activeElement;
      if (active && active.matches(SELECTORS.priceInputs)) return;

      applyPriceRange();
    }, 0);
  }

  /**
   * Applies the price range on Enter.
   * @param {KeyboardEvent} event
   */
  function handlePriceInputKeydown(event) {
    if (event.key === 'Enter') applyPriceRange();
  }

  /**
   * Updates the visibility of filter elements in the actual document
   * based on the state of matching elements in a provided DocumentFragment.
   * This function is intended to be used after fetching updated HTML fragments.
   *
   * - Hides the entire filter section if all filter values have a count of 0.
   * - Otherwise, hides only those specific filter values with a count of 0.
   *
   * @param {Document|DocumentFragment} fragment
   */
  function handleFilterVisibility(fragment) {
    /** @type {NodeListOf<HTMLElement>} */
    const filters = fragment.querySelectorAll('[data-filter="list"]');

    filters.forEach((filter) => {
      const filterName = filter.getAttribute('data-filter-name');
      const filterValues = filter.querySelectorAll('[data-filter-count]');
      // Check if every filterValue has count 0
      const shouldHideFilter = Array.from(filterValues).every((item) => item.getAttribute('data-filter-count') === '0');

      // Find the corresponding filter in the actual document
      const documentFilter = document.querySelector(`[data-filter-name="${filterName}"]`);
      
      if (!documentFilter) return;

      // Show/hide the whole filter
      if (shouldHideFilter) {
        documentFilter.setAttribute('hidden', 'true');
      } else {
        documentFilter.removeAttribute('hidden');
      }

      // Update each filter value
      filterValues.forEach((filterValue) => {
        const valueName = filterValue.getAttribute('data-filter-value');
        const valueCount = filterValue.getAttribute('data-filter-count');
        if (!valueName) return;

        const documentFilterValue = documentFilter.querySelector(`[data-filter-value="${valueName}"]`);
        if (!documentFilterValue) return;
        const documentFilterValueInput = documentFilterValue.querySelector('input');

        documentFilterValue.setAttribute('data-filter-count', valueCount);
        documentFilterValue.querySelector('[data-filter-item-count]').textContent = valueCount;

        const disabled = filterValue.getAttribute('data-filter-count') === '0';
        setFilterDisabled({input: documentFilterValueInput, disabled: disabled });
      });
    });
  }

  /**
   * Disables all filter, price, and sort input elements.
   */
  function disable() {
    const filterInputNodes = Array.from(document.querySelectorAll(`${SELECTORS.inputs}`));
    const priceInputNodes = Array.from(document.querySelectorAll(SELECTORS.priceInputs));
    const sortInputNodes = Array.from(document.querySelectorAll(SELECTORS.sortInputs));
    const elementsToDisable = filterInputNodes.concat(priceInputNodes, sortInputNodes);

    elementsToDisable.forEach((input) => {
      setFilterDisabled({input, disabled: true});
    });
  }

  /**
   * Enables all filter, price, and sort input elements.
   */
  function enable() {
    const filterInputNodes = Array.from(document.querySelectorAll(`${SELECTORS.inputs}`));
    const priceInputNodes = Array.from(document.querySelectorAll(SELECTORS.priceInputs));
    const sortInputNodes = Array.from(document.querySelectorAll(SELECTORS.sortInputs));
    const elementsToEnable = filterInputNodes.concat(priceInputNodes, sortInputNodes);

    elementsToEnable.forEach((input) => {
      setFilterDisabled({input, disabled: false});
    });
  }

  /**
   * Sets the disabled state for a filter input and updates ARIA/data attributes on its parent.
   * @param {Object} opts
   * @param {HTMLElement} opts.input
   * @param {boolean} opts.disabled
   */
  function setFilterDisabled({input, disabled}) {
    const parent = input.closest('li') || input.parentElement;

    if (disabled) {
      input.disabled = true;
      parent.setAttribute('aria-disabled', 'true');
      parent.setAttribute('data-filter-disabled', 'true');
    } else {
      input.removeAttribute('disabled');
      parent.removeAttribute('aria-disabled');
      parent.removeAttribute('data-filter-disabled');
    }
  }

  /**
   * Fetches and replaces filter components from the server (for when filters themselves change).
   * @async
   */
  async function updateFilters() {
    const existingParams = new URLSearchParams(window.location.search);
    existingParams.set('type', 'product');

    const response = await fetch(`${window.location.pathname}?${existingParams.toString()}`);
    const text = await response.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const newFilter = doc.querySelector(SELECTORS.filterComponent);
    const oldFilter = document.querySelector(SELECTORS.filterComponent);

    oldFilter.replaceWith(newFilter);
  }

  function debounce(func, wait, immediate) {
    var timeout;
    return function() {
      var context = this, args = arguments;
      var later = function() {
        timeout = null;
        if (!immediate) func.apply(context, args);
      };
      var callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func.apply(context, args);
    };
  }
})();
